import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import alertsHelper from "../../../../API/Alerts/alertsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory } from "../../../../Router";

const AlertsList = () => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const { getActiveWorkspace } = useWorkspaces();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [showChoice, setShowChoice] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = useState(false);

  const workspaceID = getActiveWorkspace()?.id;

  const loadAlerts = useCallback(async () => {
    if (!workspaceID) return;
    try {
      setIsLoading(true);
      const items = await alertsHelper.getAlertsByWorkspace(workspaceID);
      setAlerts(items || []);
    } catch (error) {
      console.error("Error loading alerts:", error);
      setAlerts([]);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceID]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const navigateToBuilder = useCallback(
    (id) => {
      const wsId = getActiveWorkspace()?.id;
      if (!wsId) return;
      history.push(`/workspaces/${wsId}/alerts/${id}`);
    },
    [getActiveWorkspace, history],
  );

  const openCreate = useCallback(() => {
    navigateToBuilder("new");
  }, [navigateToBuilder]);

  const openEdit = useCallback(
    (item) => {
      navigateToBuilder(item?.id);
    },
    [navigateToBuilder],
  );

  const handleDelete = async () => {
    try {
      setShowChoice(false);
      if (selectedItem?.id) {
        await alertsHelper.deleteAlert(selectedItem.id);
        AppToasty.show(t("alerts.deleted"), { type: "success" });
        await loadAlerts();
      }
    } catch (error) {
      console.error(error);
      AppToasty.show(t("alerts.deleteFailed"), { type: "danger" });
    }
  };

  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedAlertIds) {
        await alertsHelper.deleteAlert(id);
      }
      setSelectedAlertIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("alerts.deleted"), { type: "success" });
      await loadAlerts();
    } catch (error) {
      console.error(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("alerts.deleteFailed"), { type: "danger" });
    }
  };

  const handleDuplication = async (item) => {
    try {
      const fullRecord = alerts.find((a) => a?.id === item?.id) || item;
      if (!fullRecord) {
        AppToasty.show(t("alerts.duplicateFailed"), { type: "danger" });
        return;
      }
      const newAlert = {
        ...fullRecord,
        name: `${fullRecord.name} - Copy`,
        workspaceID: getActiveWorkspace()?.id,
      };
      delete newAlert.id;
      delete newAlert.createdAt;
      delete newAlert.updatedAt;
      delete newAlert._version;
      delete newAlert._deleted;
      delete newAlert._lastChangedAt;
      delete newAlert.__typename;
      delete newAlert.lastTriggeredAt;

      const result = await alertsHelper.createAlert(newAlert);
      if (result?.data?.createAlert?.id || result) {
        await loadAlerts();
        AppToasty.show(t("alerts.duplicated"), { type: "success" });
      }
    } catch (error) {
      console.error(error);
      AppToasty.show(t("alerts.duplicateFailed"), { type: "danger" });
    }
  };

  const columns = [
    { title: t("settings.list.id"), width: 0.00001 },
    { title: t("settings.list.name"), width: 0.24 },
    { title: t("alerts.type"), width: 0.14 },
    { title: t("alerts.severity"), width: 0.14 },
    { title: t("alerts.status"), width: 0.12 },
    { title: t("alerts.lastTriggered"), width: 0.26 },
    { title: t("settings.list.actions"), isAction: true, width: 0.1 },
  ];

  const _deleteChoice = useMemo(
    () => (
      <ToothFairyChoice
        cancelCaption={t("alerts.cancel")}
        caption={t("alerts.deleteConfirmation")}
        subCaption={t("alerts.deleteConfirmationMessage")}
        confirmCaption={t("alerts.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={handleDelete}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showChoice, selectedItem],
  );

  const _bulkDeleteChoice = useMemo(
    () => (
      <ToothFairyChoice
        cancelCaption={t("alerts.cancel")}
        caption={t("alerts.deleteConfirmation")}
        subCaption={t("alerts.deleteConfirmationMessage")}
        confirmCaption={t("alerts.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showBulkDeleteChoice, selectedAlertIds],
  );

  return (
    <View style={{ minWidth: "100%", paddingLeft: 10, flex: 1 }}>
      {isLoading ? (
        <ToothFairyLoadingPlaceholder
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
          caption={t("alerts.loadingAlerts")}
        />
      ) : alerts.length > 0 ? (
        <AppTable
          isSelectable
          showPagination
          itemsPerPage={50}
          hideSearch={false}
          searchPlaceholder={t("alerts.searchPlaceholder")}
          onSelectionChange={(selectedItems) => {
            setSelectedAlertIds(selectedItems?.map((u) => u?.id) || []);
          }}
          bulkActions={[
            {
              id: "DELETE",
              label: t("settings.list.delete"),
              icon: AppAssets.icons.delete,
              isDestructive: true,
            },
          ]}
          onBulkActionPress={(id) => {
            if (id === "DELETE" && selectedAlertIds?.length > 0)
              setShowBulkDeleteChoice(true);
          }}
          actionButtons
          mapIconWithStatus={() => AppAssets.icons.notification}
          mainActionLabel={t("alerts.view")}
          showDropDownMenuForActions
          onClickMainAction={(item) => openEdit(item)}
          onActionItemPress={async (id, item) => {
            if (id === "EDIT") {
              openEdit(item);
            } else if (id === "DUPLICATE") {
              handleDuplication(item);
            } else if (id === "DELETE") {
              setSelectedItem(item);
              setShowChoice(true);
            }
          }}
          actions={[
            {
              label: t("settings.list.edit"),
              id: "EDIT",
              icon: AppAssets.icons.edit,
            },
            {
              label: t("settings.list.duplicate"),
              id: "DUPLICATE",
              icon: AppAssets.icons.duplicate,
            },
            {
              label: t("alerts.delete"),
              id: "DELETE",
              icon: AppAssets.icons.delete,
            },
          ]}
          isItemActionable={(item) => !!item?.item}
          style={{
            zIndex: -1,
            marginTop: 15,
            height: alerts.length >= 50 ? height - 95 : height - 75,
            paddingRight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
          headers={columns}
          items={alerts
            .slice()
            .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
            .map((u) => ({
              id: u?.id || " ",
              name: u?.name || " ",
              triggerType: u?.triggerType || t("alerts.notSet"),
              severity: u?.severity || t("alerts.notSet"),
              isActive:
                u?.isActive === false
                  ? t("alerts.statusPaused")
                  : t("alerts.statusActive"),
              lastTriggered: u?.lastTriggeredAt
                ? new Date(u.lastTriggeredAt).toLocaleString()
                : t("alerts.notSet"),
              actions: " ",
            }))}
        >
          <ToothFairyButton title={t("common.create")} onPress={openCreate} />
        </AppTable>
      ) : (
        <AppPlaceholderView
          wrapperStyle={{
            width: "100%",
            height: "100%",
            marginVertical: 80,
            marginHorizontal: "auto",
            flex: 1,
          }}
          size={30}
          icon={AppAssets.icons.notification}
          mainCaption={t("alerts.noAlertsFound")}
          subCaption={t("alerts.noAlertsFoundSub")}
          actionLabel={t("common.create")}
          onActionPress={openCreate}
        />
      )}
      {_deleteChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default AlertsList;
